// Personel yetki ağacı: sidebar menüleri ve onların alt kırılımları ile birebir eşleşir.
// Buradaki `key` değerleri BE'deki MerchantUserRoleKey enum'ı ile aynı olmak zorunda,
// aksi halde /merchant-users/roles isteği 400 döner.
export const PERMISSION_SECTIONS = [
  {
    key: "PRODUCTS",
    titleKey: "settingsPage.products",
    desc1Key: "settingsPage.productsDesc1",
    desc2Key: "settingsPage.productsDesc2",
    children: [
      {
        key: "PRODUCTS_LIST",
        titleKey: "navbar.menuItems.products",
        titleFallback: "Ürünler",
      },
      {
        key: "PRODUCTS_INTEGRATIONS",
        titleKey: "navbar.menuItems.integrations",
        titleFallback: "Entegrasyonlar",
      },
    ],
  },
  {
    key: "CLIENTS",
    titleKey: "settingsPage.clients",
    desc1Key: "settingsPage.clientsDesc1",
    desc2Key: "settingsPage.clientsDesc2",
    children: [
      {
        key: "CLIENTS_INDIVIDUAL",
        titleKey: "navbar.menuItems.individual",
        titleFallback: "Bireysel Müşteriler",
      },
      {
        key: "CLIENTS_CORPORATE",
        titleKey: "navbar.menuItems.corporate",
        titleFallback: "Kurumsal Müşteriler",
      },
    ],
  },
  {
    key: "ACTIVATIONS",
    titleKey: "settingsPage.additionalServices",
    desc1Key: "settingsPage.additionalServicesDesc1",
    desc2Key: "settingsPage.additionalServicesDesc2",
    children: [
      {
        key: "ACTIVATIONS_SALES",
        titleKey: "navbar.menuItems.sales",
        titleFallback: "Satışlar",
      },
      {
        key: "ACTIVATIONS_PACKAGES",
        titleKey: "navbar.menuItems.packages",
        titleFallback: "Paketler",
      },
      {
        key: "ACTIVATIONS_REPORTS",
        titleKey: "navbar.menuItems.saleReports",
        titleFallback: "Raporlar",
      },
      {
        key: "ACTIVATIONS_SETTINGS",
        titleKey: "settingsPage.activations",
        titleFallback: "Ek Hizmet Ayarları",
      },
    ],
  },
  {
    key: "CLAIMS",
    titleKey: "settingsPage.claims",
    desc1Key: "settingsPage.claimsDesc1",
    desc2Key: "settingsPage.claimsDesc2",
    children: [
      {
        key: "CLAIMS_SERVICE",
        titleKey: "settingsPage.serviceClaims",
        titleFallback: "Servis Talepleri",
      },
      {
        key: "CLAIMS_SETUP",
        titleKey: "settingsPage.setupClaims",
        titleFallback: "Kurulum Talepleri",
      },
      {
        key: "CLAIMS_SERVICE_RECORD",
        titleKey: "settingsPage.serviceRecords",
        titleFallback: "Servis Kayıtları",
      },
    ],
  },
  {
    key: "CAMPAIGNS",
    // Kampanyalar menüsü bugüne kadar yetkiye bağlı değildi, yani herkese açıktı.
    // Kaydı olmayan personellerde ekran bu yetkiyi işaretli açar; aksi halde
    // personel kaydedildiğinde mevcut erişim sessizce kaldırılırdı.
    legacyOpenAccess: true,
    titleKey: "navbar.menuItems.campaigns",
    titleFallback: "Kampanyalar",
    desc1Key: "settingsPage.campaignsDesc1",
    desc1Fallback: "Kampanyaları görüntüleyebilir.",
    desc2Key: "settingsPage.campaignsDesc2",
    desc2Fallback: "Kampanya oluşturabilir ve düzenleyebilir.",
  },
  {
    key: "APPS",
    titleKey: "settingsPage.applications",
    desc1Key: "settingsPage.applicationsDesc1",
    desc2Key: "settingsPage.applicationsDesc2",
  },
  {
    key: "DOCUMENTS",
    titleKey: "settingsPage.documents",
    desc1Key: "settingsPage.documentsDesc1",
    desc2Key: "settingsPage.documentsDesc2",
  },
  {
    key: "PERSONALS",
    titleKey: "settingsPage.employees",
    desc1Key: "settingsPage.employeesDesc1",
    desc2Key: "settingsPage.employeesDesc2",
  },
];

export const ALL_PERMISSION_KEYS = PERMISSION_SECTIONS.flatMap((section) => [
  section.key,
  ...(section.children || []).map((child) => child.key),
]);

const PARENT_OF = PERMISSION_SECTIONS.reduce((acc, section) => {
  (section.children || []).forEach((child) => {
    acc[child.key] = section.key;
  });
  return acc;
}, {});

export const getParentPermissionKey = (key) => PARENT_OF[key];

// Alt kırılımlar sonradan eklendi; kaydı olmayan personellerde üst menünün yetkisi
// geçerli sayılır, o da yoksa (eski davranış) erişim açık bırakılır.
export const hasPermission = (roles, section, action) => {
  const parentKey = PARENT_OF[section];
  const sectionRoles =
    roles?.[section] ?? (parentKey ? roles?.[parentKey] : undefined);

  return !sectionRoles || sectionRoles.includes(action);
};

export const emptyPermissionState = () =>
  ALL_PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {});

const SECTION_BY_KEY = PERMISSION_SECTIONS.reduce((acc, section) => {
  acc[section.key] = section;
  return acc;
}, {});

const withRole = (roles = [], role) =>
  roles.includes(role) ? roles : [...roles, role];

const withoutRole = (roles = [], role) => roles.filter((r) => r !== role);

export const isAllPermissionsSelected = (permissions) =>
  ALL_PERMISSION_KEYS.every((key) => permissions[key]?.length === 2);

export const isNoPermissionSelected = (permissions) =>
  ALL_PERMISSION_KEYS.every((key) => !permissions[key]?.length);

export const toggleAllPermissions = (permissions) => {
  const grantAll = isNoPermissionSelected(permissions);

  return ALL_PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = grantAll ? ["VIEW", "EDIT"] : [];
    return acc;
  }, {});
};

// Üst menü kapatıldığında alt kırılımlar da kapanır, açıldığında ise aynı yetki
// alt kırılımlara dağıtılır: üst menü grubun "tümünü seç" davranışını taşır.
export const toggleSectionPermission = (permissions, sectionKey, action) => {
  const childKeys = (SECTION_BY_KEY[sectionKey]?.children || []).map(
    (child) => child.key,
  );
  const isSelected = permissions[sectionKey]?.includes(action);
  const next = { ...permissions };

  const apply = (key) => {
    if (action === "VIEW") {
      next[key] = isSelected ? [] : withRole(permissions[key], "VIEW");
    } else {
      next[key] = isSelected
        ? withoutRole(permissions[key], "EDIT")
        : withRole(withRole(permissions[key], "VIEW"), "EDIT");
    }
  };

  apply(sectionKey);
  childKeys.forEach(apply);

  return next;
};

// Alt kırılıma yetki verildiğinde üst menü görünmüyorsa sayfaya erişilemez,
// bu yüzden üst menünün görüntüleme yetkisi otomatik açılır.
export const toggleChildPermission = (permissions, childKey, action) => {
  const isSelected = permissions[childKey]?.includes(action);
  const next = { ...permissions };

  if (action === "VIEW") {
    next[childKey] = isSelected ? [] : withRole(permissions[childKey], "VIEW");
  } else {
    next[childKey] = isSelected
      ? withoutRole(permissions[childKey], "EDIT")
      : withRole(withRole(permissions[childKey], "VIEW"), "EDIT");
  }

  const parentKey = PARENT_OF[childKey];
  if (parentKey && next[childKey].length) {
    next[parentKey] = withRole(permissions[parentKey], "VIEW");
  }

  return next;
};

// Alt kırılımlar sonradan eklendiği için eski personellerde bu kayıtlar yok.
// Kayıt yoksa üst menünün yetkisi devralınır; böylece yetki ekranı ilk açıldığında
// personelin mevcut erişimi daraltılmış görünmez.
export const permissionStateFromRoles = (roles = {}) =>
  PERMISSION_SECTIONS.reduce((acc, section) => {
    const parentRoles =
      roles?.[section.key] ||
      (section.legacyOpenAccess ? ["VIEW", "EDIT"] : []);
    acc[section.key] = [...parentRoles];
    (section.children || []).forEach((child) => {
      acc[child.key] = [...(roles?.[child.key] || parentRoles)];
    });
    return acc;
  }, {});
